import {
  METRICS_INTERFACE_OPER_STATUS_MAP,
  METRICS_INTERFACE_TYPES,
  METRICS_STATUS_LABEL_MAP
} from 'app/stores/metrics/metricsConstants';
import api from 'core/util/api';
import MetricDeviceModel from 'app/stores/metrics/MetricDeviceModel';
import $query from 'app/stores/query/$query';
import MetricConnectionCollection from 'app/stores/metrics/MetricConnectionCollection';

export interface TopologyInterface {
  device_id: number;
  layer2_override_far_end_device?: unknown;
  layer2_override_far_end_snmp_id?: unknown;
  layer2_override_far_end_display_name?: unknown;
  snmp_id?: number;
  device_name?: string;
  interface_description: string;
  interface_ip: string;
  admin_status: string;
  if_oper_status: string;
  snmp_alias: string;
  connectivity_type: string;
  network_boundary: string;
  snmp_speed: number;
  lldp_rem_sys_name?: string;
  lldp_rem_port_id?: string;
}

export interface TopologyConnection {
  layer?: number;
  linkMetadata?: unknown;
  interface1?: TopologyInterface;
  interface2?: TopologyInterface;
}

export type TopologyConnections = {
  connections: TopologyConnection[];
}[];

export interface TopologyData {
  deviceLinks: TopologyConnections;
  siteLinks: TopologyConnections;
  externalLinks: TopologyConnections;
}

export const getMetricValueLabel = (value: string) => METRICS_STATUS_LABEL_MAP[value] || value;

export const fetchDeviceFilterOptions = (options = {}) =>
  api.get('/api/ui/recon/options/devices', { query: options }).then((results) => {
    const alphaSort = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);

    results.site_id = (results.site_id || []).sort(alphaSort);
    results.location = (results.location || []).sort(alphaSort);
    results.vendor = (results.vendor || []).sort(alphaSort);
    results.model = (results.model || []).sort(alphaSort);
    results.status = (results.status || []).map(({ value }: { value: string }) => ({
      value,
      label: METRICS_STATUS_LABEL_MAP[value] || value
    }));

    return results;
  });

export const fetchInterfaceFilterOptions = (options = {}) =>
  api.get('/api/ui/recon/options/interfaces', { query: options }).then((results) => {
    results.oper_status = (results.oper_status || []).map(({ value }: { value: string }) => ({
      value,
      label: METRICS_STATUS_LABEL_MAP[value] || value
    }));

    results.admin_status = (results.admin_status || []).map(({ value }: { value: string }) => ({
      value,
      label: METRICS_STATUS_LABEL_MAP[value] || value
    }));

    results.type = (results.type || [])
      .map(({ value }: { value: string }) => ({
        value,
        label: METRICS_INTERFACE_TYPES[value] || value
      }))
      .sort((a: { value: string; label: string }, b: { value: string; label: string }) =>
        a.label.localeCompare(b.label)
      );

    results.mtu = (results.mtu || []).sort(
      (a: { value: string }, b: { value: string }) => Number(a.value) - Number(b.value)
    );

    return results;
  });

export const fetchDevicesAvailability = (options = {}) =>
  api.get('/api/ui/recon/devicesAvailability', { query: options }).then((response) => response);

export const fetchDeviceBgpData = (device: MetricDeviceModel, lookback = 'P1D') => {
  device.bgpData = null;
  return api.get(`/api/ui/recon/devices/${device.id}/bgpData?lookback=${lookback}`).then((bgpData) => {
    device.bgpData = bgpData;
  });
};

export const fetchAsnsWithAsGroups = (asns: string[]) =>
  api.get('/api/ui/asns/bulk', {
    query: {
      asns
    }
  });

export const fetchDeviceUptime = (deviceModel: MetricDeviceModel) =>
  $query.runQuery(deviceModel.uptimeQuery)?.then(({ results }) => {
    const [rawResults] = (results as null | { toJS: () => [Record<string, string>] })?.toJS() || [];

    if (rawResults) {
      deviceModel.set('bootTime', rawResults['boot-time']);
      deviceModel.set('uptime', rawResults.uptime);
    }
  });

export const fetchDevice = (deviceId: string) => {
  const device = new MetricDeviceModel({ id: deviceId });

  return device
    .fetch()
    .then(() => $query.runQuery(device.deviceStatusQuery))
    .then((results) => {
      const rawResults = (results?.results as undefined | { toJS: () => [{ last_available: unknown }] })?.toJS();
      const available = rawResults?.[0]?.last_available;

      if (available !== undefined) {
        device.set('available', available);
      } else {
        console.warn('available missing:', available);
      }
    })
    .then(() => device);
};

export const processTopologyDataForDevice = (device: MetricDeviceModel, topologyData: TopologyData | undefined) => {
  if (!topologyData) {
    console.warn('No topology data to process for device', device.id);
    device.connections = new MetricConnectionCollection([], { parent: device });
    device.set('connectionCount', 0);
    return device;
  }

  // combine all the connections into a single array
  const { deviceLinks, siteLinks, externalLinks } = topologyData;
  const flatConnections = [...deviceLinks, ...siteLinks, ...externalLinks]
    .map((t) => t.connections)
    .flat()
    // only l2 connections for now
    .filter((t) => t.layer === 2);

  function getInterfaceMetadata(connection: TopologyConnection, key: 'interface1' | 'interface2') {
    if (!connection[key]) {
      return {};
    }

    return {
      interface_description: connection[key].interface_description,
      interface_ip: connection[key].interface_ip,
      admin_status: METRICS_INTERFACE_OPER_STATUS_MAP[parseInt(connection[key].admin_status)],
      oper_status: METRICS_INTERFACE_OPER_STATUS_MAP[parseInt(connection[key].if_oper_status)],
      snmp_alias: connection[key].snmp_alias,
      connectivity_type: connection[key].connectivity_type,
      network_boundary: connection[key].network_boundary,
      snmp_speed: connection[key].snmp_speed,
      lldp_rem_sys_name: connection[key].lldp_rem_sys_name,
      lldp_rem_port_id: connection[key].lldp_rem_port_id
    };
  }

  // we first need to determine which key is the "local" interface
  // sometimes the interface1 is the local interface, sometimes it's interface2

  // we only need the device name and interface ID for the connections
  const connections = flatConnections.map((connection) => {
    // do not care about layer and linkMetadata for now
    delete connection.layer;
    delete connection.linkMetadata;

    let localKey: 'interface1' | 'interface2' = 'interface2';
    let remoteKey: 'interface1' | 'interface2' = 'interface1';

    if (connection.interface1?.device_id === parseInt(String(device.id))) {
      localKey = 'interface1';
      remoteKey = 'interface2';
    }

    // check if we have any overrides for the device or interface
    const device_local_override = !!connection[localKey]?.layer2_override_far_end_device;
    const interface_local_override = !!connection[localKey]?.layer2_override_far_end_snmp_id;
    const remote_display_name_override = !!connection[localKey]?.layer2_override_far_end_display_name;

    const device_remote_override = !!connection[remoteKey]?.layer2_override_far_end_device;
    const interface_remote_override = !!connection[remoteKey]?.layer2_override_far_end_snmp_id;

    const remote_display_name = connection[localKey]?.layer2_override_far_end_display_name;

    const device_local = device.id;
    const interface_local = connection[localKey]?.snmp_id;

    const device_remote = connection[remoteKey]?.device_id;
    const interface_remote = connection[remoteKey]?.snmp_id;

    return {
      id: `${device_local}-${interface_local}-${device_remote}-${interface_remote}`,

      device_local,
      device_local_override,

      interface_local,
      interface_local_override,

      device_remote,
      device_remote_override,

      interface_remote,
      interface_remote_override,

      remote_display_name,
      remote_display_name_override,

      device_local_metadata: {
        device_name: device.get('device_name') || device.get('name')
      },

      device_remote_metadata: {
        device_name: connection[remoteKey]?.device_name
      },

      // some additional metadata for the interfaces
      interface_local_metadata: getInterfaceMetadata(connection, localKey),
      interface_remote_metadata: getInterfaceMetadata(connection, remoteKey)
    };
  });

  device.connections = new MetricConnectionCollection(connections, { parent: device });
  device.set('connectionCount', connections.length);
  device.set(
    'manualConnectionCount',
    connections.filter(
      (c) =>
        c.device_local_override ||
        c.interface_local_override ||
        c.remote_display_name_override ||
        c.device_remote_override ||
        c.interface_remote_override
    ).length
  );
  return device;
};

export const saveInterfaceConnection = ({
  local_device_id,
  local_interface_snmp_id,
  remote_device_id,
  remote_interface_snmp_id,
  remote_display_name,
  remove_connection = false
}: {
  local_device_id: string;
  local_interface_snmp_id: string;
  remote_device_id: string;
  remote_interface_snmp_id: string;
  remote_display_name: string;
  remove_connection?: boolean;
}) =>
  api.post('/api/ui/topology/connection', {
    data: {
      local_device_id,
      local_interface_snmp_id,
      remote_device_id,
      remote_interface_snmp_id,
      remote_display_name,
      remove_connection
    }
  });
